import type { OptionGroup } from "@/types";

/**
 * Lowest reachable price for a required option group.
 *
 * Rules:
 * - Only required groups (`minPermitted > 0`) contribute.
 * - Honors each option's `maxPermitted` (0 = unlimited).
 * - Applies the group's `priceMethod`: SUM adds; a portion group bills its
 *   slots; any other AVG/MAX group charges the mean or top price once.
 *
 * @param priceKey  "value" for current price, "originalValue" for pre-discount.
 */
export function calcMinGroupPrice(
  opg: OptionGroup,
  priceKey: "value" | "originalValue" = "value",
): number {
  if (opg.minPermitted <= 0 || !opg.options || opg.options.length === 0) {
    return 0;
  }

  const sortedOptions = [...opg.options]
    .filter((opt) => {
      if (priceKey === "originalValue") return opt.price.originalValue != null;
      return true;
    })
    .sort((a, b) => {
      const priceA = priceKey === "originalValue" ? (a.price.originalValue ?? 0) : a.price.value;
      const priceB = priceKey === "originalValue" ? (b.price.originalValue ?? 0) : b.price.value;
      return priceA - priceB;
    });

  if (sortedOptions.length === 0) return 0;

  let remaining = opg.minPermitted;
  const selectedPrices: number[] = [];

  for (const opt of sortedOptions) {
    if (remaining <= 0) break;

    const price =
      priceKey === "originalValue" ? (opt.price.originalValue ?? 0) : opt.price.value;

    const canSelect = Math.min(
      remaining,
      opt.maxPermitted > 0 ? opt.maxPermitted : remaining,
    );

    for (let i = 0; i < canSelect; i++) {
      selectedPrices.push(price);
    }
    remaining -= canSelect;
  }

  if (selectedPrices.length === 0) return 0;

  const total = selectedPrices.reduce((sum, p) => sum + p, 0);
  switch (opg.priceMethod) {
    case "MAX":
      return Math.max(...selectedPrices);
    case "AVG":
      return total / selectedPrices.length;
    case "SUM":
    default:
      return total;
  }
}
